import React, { useMemo, useState } from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { Edit, Trash2 } from 'react-feather';
import 'react-datepicker/dist/react-datepicker.css';

const ClientCommunicationHistoryGrid = ({ history = [], isLoading, hasClientSelected, onMarkCompleted, onEditEntry, onDeleteEntry }) => {
  const [searchText, setSearchText] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [editRemark, setEditRemark] = useState('');
  const [editFollowupDate, setEditFollowupDate] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const filteredHistory = useMemo(() => {
    if (!searchText) {
      return history;
    }
    const term = searchText.toLowerCase();
    return history.filter((entry) => {
      const remark = entry.Remark ? entry.Remark.toLowerCase() : '';
      const companyTypes = entry.ClientCompanyTypeNames
        ? entry.ClientCompanyTypeNames.toLowerCase()
        : '';
      const recordedBy = entry.CreatedByName ? entry.CreatedByName.toLowerCase() : '';
      return (
        remark.includes(term) ||
        companyTypes.includes(term) ||
        recordedBy.includes(term)
      );
    });
  }, [history, searchText]);

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setEditRemark(entry?.Remark || '');
    setEditFollowupDate(entry?.NextFollowupDate ? new Date(entry.NextFollowupDate) : null);
  };

  const closeEditModal = () => {
    setEditingEntry(null);
    setEditRemark('');
    setEditFollowupDate(null);
  };

  const handleSaveEdit = async () => {
    if (!onEditEntry || !editingEntry) {
      return;
    }
    if (!editRemark || !editRemark.trim()) {
      return;
    }

    setIsSavingEdit(true);
    try {
      await onEditEntry({
        ClientCommunicationId: editingEntry.ClientCommunicationId,
        ClientId: editingEntry.ClientId,
        Remark: editRemark.trim(),
        NextFollowupDate: editFollowupDate
      });
      closeEditModal();
    } finally {
      setIsSavingEdit(false);
    }
  };

  const renderHistoryCards = () =>
    filteredHistory.map((entry, index) => {
      const initials = (entry.CreatedByName || 'U')
        .split(' ')
        .map((word) => word.charAt(0))
        .join('')
        .substring(0, 2)
        .toUpperCase();

      const hasFollowup = entry.NextFollowupDate != null;
      const isCompleted = entry.IsCompleted === 1;

      return (
        <div className="col-xl-12" key={entry.ClientCommunicationId || index}>
          <div className="card card-bg-light mg-em mb-2 shadow-none border">
            <div className="card-body py-2 px-3">
              <div className="d-flex align-items-start w-100">
                <div className="me-3">
                  <span
                    className="avatar avatar-rounded bg-soft-primary text-primary fw-semibold"
                    style={{ width: 32, height: 32, fontSize: '0.8rem' }}
                  >
                    {initials}
                  </span>
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex flex-column flex-md-row justify-content-between">
                    {/* LEFT SIDE - Company type and remark */}
                    <div className="flex-grow-1">
                      <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                        {entry.ClientCompanyTypeNames || 'System'}
                      </div>
                      <p className="mb-0 mt-1 text-muted fw-semibold" style={{ fontSize: '0.95rem', fontweight: '700' }}>
                        {entry.Remark || 'No remarks added.'}
                      </p>
                    </div>

                    {/* RIGHT SIDE - Followup date, user name and communication date */}
                    <div className="text-md-end mt-2 mt-md-0">
                      {hasFollowup && (
                        <div className="d-flex align-items-center justify-content-md-end mb-1">
                          <span className={`badge ${isCompleted ? 'bg-success' : 'bg-warning'} me-2`} style={{ fontSize: '0.7rem' }}>
                            {isCompleted ? 'Completed' : 'Pending'}
                          </span>
                          <span className="text-primary fw-semibold" style={{ fontSize: '0.75rem' }}>
                            Followup: {moment(entry.NextFollowupDate).format('DD MMM YYYY, hh:mm A')}
                          </span>
                          {onMarkCompleted && (
                            <input
                              type="checkbox"
                              className="form-check-input ms-2"
                              checked={isCompleted}
                              onChange={(e) => onMarkCompleted(entry.ClientCommunicationId, e.target.checked)}
                              title={isCompleted ? "Completed" : "Mark as completed"}
                            />
                          )}
                        </div>
                      )}
                      <span className="mb-0 mt-1 text-muted fw-semibold" style={{ fontSize: '0.7rem' }}>
                        {entry.CreatedByName || 'Not specified'}
                      </span>

                      <p className="mb-0 text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                        {moment(entry.CommunicationDate).format('DD MMM YYYY, hh:mm A')}
                      </p>
                      {(onEditEntry || onDeleteEntry) && (
                        <div className="d-flex justify-content-md-end gap-2 mt-1">
                          {onEditEntry && (
                            <a
                              href="#!"
                              className="text-primary"
                              style={{ lineHeight: 1 }}
                              title="Edit"
                              aria-label="Edit followup"
                              onClick={(e) => {
                                e.preventDefault();
                                openEditModal(entry);
                              }}
                            >
                              <Edit size={14} />
                            </a>
                          )}
                          {onDeleteEntry && (
                            <a
                              href="#!"
                              className="text-danger"
                              style={{ lineHeight: 1 }}
                              title="Delete"
                              aria-label="Delete followup"
                              onClick={(e) => {
                                e.preventDefault();
                                onDeleteEntry(entry);
                              }}
                            >
                              <Trash2 size={14} />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });

  return (
    <>
      <div className="card">
        <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-md-center">
          <h5 className="card-title mb-2 mb-md-0">Communication History</h5>
          <div className="d-flex align-items-center wd-25">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search by remark, company or user"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <span className="text-muted small">Loading...</span>
          ) : filteredHistory.length === 0 ? (
            <p className="text-muted mb-0">
              {hasClientSelected
                ? 'No communication entries found for this client.'
                : 'Select a client to view history.'}
            </p>
          ) : (
            <div className="row communication-history-cards">{renderHistoryCards()}</div>
          )}
        </div>
      </div>

      {editingEntry && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Followup</h5>
                <button type="button" className="btn-close" onClick={closeEditModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Remark <span style={{ color: 'red' }}>*</span></label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={editRemark}
                    onChange={(e) => setEditRemark(e.target.value)}
                  />
                </div>
                <div className="mb-0">
                  <label className="form-label">Next Followup Date</label>
                  <DatePicker
                    selected={editFollowupDate}
                    onChange={(date) => setEditFollowupDate(date)}
                    showTimeSelect
                    timeFormat="hh:mm aa"
                    timeIntervals={15}
                    dateFormat="dd/MM/yyyy h:mm aa"
                    className="form-control"
                    placeholderText="Select followup date & time"
                    isClearable
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeEditModal} disabled={isSavingEdit}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveEdit}
                  disabled={isSavingEdit || !editRemark || !editRemark.trim()}
                >
                  {isSavingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientCommunicationHistoryGrid;

